import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import {
  ArrowRight, MapPin, Shield, Heart, Users, Building2, Star,
  Navigation, Phone, CreditCard, MessageSquare, Gift, Settings,
  Eye, Lock, FileText, ChevronRight, Check, Smartphone, HandHelping,
  Accessibility, HelpCircle, BarChart3, UserCog, Radio, Layers,
  Link2, Bell, CheckCircle2
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

/* ================================================================
   ANIMATION STYLES — injected once, respects prefers-reduced-motion
   ================================================================ */
const AnimStyles = () => (
  <style>{`
    @media (prefers-reduced-motion: no-preference) {
      @keyframes hcDrawRoute {
        from { stroke-dashoffset: 260; }
        to   { stroke-dashoffset: 0; }
      }
      @keyframes hcSlideInRight {
        from { opacity: 0; transform: translateX(32px); }
        to   { opacity: 1; transform: translateX(0); }
      }
      @keyframes hcGrowBar {
        from { transform: scaleY(0); }
        to   { transform: scaleY(1); }
      }
      @keyframes hcPulse {
        0%, 100% { opacity: .55; }
        50%      { opacity: 1; }
      }
      @keyframes hcTabFade {
        from { opacity: 0; transform: translateY(10px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes hcFloat {
        0%, 100% { transform: translateY(0); }
        50%      { transform: translateY(-6px); }
      }
      .hc-draw   { animation: hcDrawRoute 1.6s ease-out forwards; }
      .hc-slidein { animation: hcSlideInRight .7s ease-out both; }
      .hc-grow    { animation: hcGrowBar .9s ease-out both; transform-origin: bottom; }
      .hc-pulse   { animation: hcPulse 2.4s ease-in-out infinite; }
      .hc-tabfade { animation: hcTabFade .35s ease-out; }
      .hc-float   { animation: hcFloat 3s ease-in-out infinite; }
    }
    @media (prefers-reduced-motion: reduce) {
      .hc-draw, .hc-slidein, .hc-grow, .hc-pulse, .hc-tabfade, .hc-float {
        animation: none !important;
        opacity: 1 !important;
        transform: none !important;
      }
    }
    /* Flex-grid: last row stretches to fill — one solution for all card grids */
    .hc-fg { display: flex; flex-wrap: wrap; }
    .hc-fg > * { flex-grow: 1; min-width: 0; }
    /* 3-col variant (gap 1.5rem = gap-6) */
    .hc-fg3 { gap: 1.5rem; }
    .hc-fg3 > * { flex-basis: 100%; }
    @media (min-width: 768px) { .hc-fg3 > * { flex-basis: calc(50% - 0.75rem); } }
    @media (min-width: 1024px) { .hc-fg3 > * { flex-basis: calc(33.333% - 1rem); } }
    /* 4-col variant (gap 1rem = gap-4) */
    .hc-fg4 { gap: 1rem; }
    .hc-fg4 > * { flex-basis: 100%; }
    @media (min-width: 640px) { .hc-fg4 > * { flex-basis: calc(50% - 0.5rem); } }
    @media (min-width: 1024px) { .hc-fg4 > * { flex-basis: calc(33.333% - 0.667rem); } }
    @media (min-width: 1280px) { .hc-fg4 > * { flex-basis: calc(25% - 0.75rem); } }
  `}</style>
);

/* ================================================================
   SCROLL-REVEAL WRAPPER
   ================================================================ */
const Reveal = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setVis(true); return; }
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVis(true); obs.disconnect(); }
    }, { threshold: 0.12 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={className}
      style={{ opacity: vis ? 1 : 0, transform: vis ? 'translateY(0)' : 'translateY(28px)',
        transition: `opacity .65s ease ${delay}s, transform .65s ease ${delay}s` }}>
      {children}
    </div>
  );
};

/* ================================================================
   PAGE-LOCAL TRANSLATIONS — only this file is affected
   ================================================================ */
const T = {
  en: {
    heroLabel: 'Tapelligence for Hospitals',
    heroTitle: 'One tap from the parking lot to the foundation.',
    heroSub: 'Find the way. Ask for an orderly or security. Pay for parking. Share a review. Give to the foundation. No app, no login.',
    heroCta: 'Book a live demo',
    counter0: 'apps, logins or downloads',
    counter1: 'No GPS needed to start',
    counter2: 'tap to ask for an orderly or security',
    counter3: 'English, French, Spanish',

    oppLabel: 'The Opportunity',
    oppTitle: 'A hospital visit has too many small frictions.',
    oppText: 'Hospitals are large, confusing and stressful. Patients arrive anxious and late, and staff are pulled away from their work to give directions. Asking for an orderly, reaching security, paying for parking, sharing feedback or giving to the foundation each means a different desk, number or system.',
    oppIdea1Title: 'Find the way',
    oppIdea1: 'Patients and visitors see where they are and get a clear route to where they need to go.',
    oppIdea2Title: 'Get help fast',
    oppIdea2: 'A non-clinical request for an orderly, security or general help reaches the right team, with the exact location attached.',
    oppIdea3Title: 'Close the loop',
    oppIdea3: 'Parking, reviews, feedback and donations to the foundation — all from the same tap.',

    howLabel: 'How It Works',
    howTitle: 'One connected touchpoint network for the whole hospital.',
    howStep1Title: 'Tap',
    howStep1: 'Tap a touchpoint or scan a code. Nothing to install. A clean, multilingual page opens and knows exactly where you are.',
    howStep2Title: 'Choose',
    howStep2: 'Find the way, ask for help, pay for parking, share feedback or give to the foundation.',
    howStep3Title: 'Control',
    howStep3: 'Requests reach the right team with the location attached. One Command Center sees it all.',
    howTouchpoints: 'Touchpoints at entrances, parking, elevators, corridors and waiting areas.',

    foundLabel: 'The Foundation',
    foundTitle: 'Every lobby, waiting room and elevator becomes a moment to give.',
    foundP1Title: 'A gift is one tap away',
    foundP1: 'Place donation touchpoints where people have a moment and a reason to give.',
    foundP2Title: 'Gratitude moments',
    foundP2: 'Patients and families who had a good experience are one tap from giving back.',
    foundP3Title: 'The donor relationship stays yours',
    foundP3: 'The gift goes to the foundation\'s own donation page, which keeps control of donor relationships.',
    foundP4Title: 'Reporting by location',
    foundP4: 'See which touchpoints inspire the most interest, with no personal data held by Tapelligence.',
    foundP5Title: 'Simple and multilingual',
    foundP5: 'A short gift path in English, French or Spanish.',

    audLabel: 'Who Benefits',
    audTitle: 'Built for everyone the hospital touches.',
    audTab1: 'Hospital & Administration',
    audTab2: 'Patients, Visitors & Caregivers',
    audTab3: 'Staff',
    audTab4: 'The Foundation',
    audHosp: [
      'Better first impressions — patients and families feel guided from the parking lot to the right door.',
      'Fewer late arrivals and wrong turns on the way to appointments.',
      'Stronger reviews — satisfied patients are invited to leave a public review; concerns are raised privately.',
      'Operational insight — top destinations, busiest touchpoints and where people ask for help, with no personal data.',
      'A full audit trail on every request, change and action.',
    ],
    audPatients: [
      'Tap or scan and it works — no app, account or password.',
      'Know where you are — the touchpoint tells the phone exactly where you are standing.',
      'A clear route with distance and estimated walking time.',
      'Routes designed for accessible routing, high contrast and simple screens.',
      'Ask for help — request an orderly or security assistance without hunting for someone.',
      'Pay for parking from the phone on the way out.',
      'A quick review or private feedback, right when it is fresh.',
    ],
    audStaff: [
      'Orderly requests arrive with the exact location and an acknowledgement.',
      'Security receives non-emergency assistance requests with the location attached.',
      'Fewer "where is\u2026?" questions — people help themselves.',
      'Maps are maintained from a simple admin tool, designed for non-technical staff.',
      'Role-based access — each person sees only what their role needs.',
    ],
    audFound: 'The full giving story is in the Foundation section above.',

    capLabel: 'Platform Capabilities',
    capTitle: 'One platform, every touchpoint.',
    caps: [
      { title: 'Tap Points', desc: 'Discreet NFC tags and QR codes at entrances, parking, elevators, corridors and waiting areas. Each one knows its building, floor and area.' },
      { title: 'Find Your Way', desc: 'See the exact location on a map with no GPS, then pick a destination.' },
      { title: 'Take Me There', desc: 'Step-by-step indoor route with distance and estimated walking time.' },
      { title: 'Accessible Routing', desc: 'Wheelchair-accessible routes, high contrast and simple screens.' },
      { title: 'Request an Orderly', desc: 'A non-clinical request with the exact location and acknowledgement.' },
      { title: 'Request Security', desc: 'Non-emergency assistance with the location attached.' },
      { title: 'General Help', desc: 'A request for non-clinical help, routed to reception or volunteers.' },
      { title: 'Pay for Parking', desc: 'Optional connection to the hospital\'s parking system.' },
      { title: 'Reviews & Feedback', desc: 'Public reviews for satisfied patients; private concerns for fast resolution.' },
      { title: 'Donate to the Foundation', desc: 'A simple gift path to the foundation\'s own donation page.' },
      { title: 'Map Admin', desc: 'Staff update destinations and touchpoints themselves, without a developer.' },
      { title: 'Command Center', desc: 'Live view of requests, top destinations and busiest touchpoints.' },
      { title: 'Roles & Governance', desc: 'Super admin, client admin, site manager, staff and read-only observer.' },
      { title: 'Live Positioning', desc: 'Optional upgrade with beacons or Wi-Fi for a live blue dot and re-routing.', roadmap: true },
    ],

    journeyLabel: 'The Journey',
    journeyTitle: 'From the parking lot to the foundation.',
    journeySteps: ['Arrive', 'Find the way', 'Ask for help', 'Pay for parking', 'Share', 'Give back'],

    whoLabel: 'Who It Is For',
    whoTitle: 'From a community hospital to a health system.',
    who: [
      { type: 'Community hospitals', points: ['Fast start at key entrances', 'Fewer lost patients', 'Light on staff and IT'] },
      { type: 'Large & teaching hospitals', points: ['Multi-building routes', 'Wings, floors and elevators', 'Central map administration'] },
      { type: 'Health systems', points: ['One dashboard, every site', 'Shared brand standards', 'Role-based access by site'] },
    ],

    trustLabel: 'Trust, Privacy & Governance',
    trustTitle: 'Designed for a sensitive environment.',
    trustItems: [
      { title: 'Non-clinical by design', text: 'No medical advice and no clinical details collected. Not a substitute for emergency services.' },
      { title: 'Privacy by design', text: 'No account. Location comes only from the touchpoint a person chooses to tap, and nothing tracks individuals.' },
      { title: 'Access control', text: 'Roles are enforced on the server, not just hidden in the interface.' },
      { title: 'Full audit trail', text: 'Every map change, request and action is logged.' },
      { title: 'Payments & donations stay with you', text: 'Parking payments and gifts are handled by the hospital\'s own systems.' },
    ],
    trustComplement: 'Works alongside existing signage and systems. No EMR or scheduling integration is needed to start.',

    nextLabel: 'Next Steps',
    nextTitle: 'A simple path from conversation to results.',
    nextSteps: [
      { title: 'Live demo', desc: 'A 20-minute demo tailored to your hospital.' },
      { title: 'Pilot', desc: 'One entrance and key routes in one building.' },
      { title: '90-day review', desc: 'Usage, requests, reviews, gifts and staff feedback.' },
      { title: 'Rollout', desc: 'Extend across wings, buildings and campuses.' },
    ],
    nextClosing: 'Let\'s start with a pilot.',
    pdfBtn: 'Download the hospital executive summary',
    pdfNote: '',

    smallPrint: [
      'Not intended for medical emergencies or clinical use. Security requests are for non-emergency assistance.',
      'Feature availability and configuration are set per hospital; some capabilities, including parking payment connections, are optional, planned or subject to integration.',
      'Privacy and data rules vary by province, state and regulator; hospitals remain responsible for the settings they enable and should confirm requirements locally.',
      'Impact is measured in the pilot and is not guaranteed.',
    ],
    roadmapBadge: 'Roadmap',
    optionalBadge: 'Optional',

    /* Visual labels — tabs */
    vizCommandCenter: 'Command Center',
    vizBusiest: 'Busiest touchpoints',
    vizTopDest: 'Top destinations',
    vizActive: 'Active requests',
    vizTeams: 'Teams online',
    vizStaff: 'Staff',
    vizSecurity: 'Security',
    vizEvs: 'EVS',
    vizVolunteers: 'Volunteers',
    vizYouAreHere: 'You are here',
    vizDestination: 'Destination',
    vizWalk: 'Walk',
    vizNewRequest: 'New request',
    vizOrderly: 'Orderly request',
    vizLocationAttached: 'Location attached',
    vizAcknowledge: 'Acknowledge',
    vizTapToGive: 'Tap to give',
    vizGiveTitle: 'Give to the foundation',
    vizGiveBtn: 'Give',
    vizFoundStoryBtn: 'See the full foundation story',
    vizPending: 'pending',
    vizEntrance: 'Entrance',
    vizLobby: 'Lobby',
    vizWaitingRoom: 'Waiting room',
    vizElevator: 'Elevator',
  },

  fr: {
    heroLabel: 'Tapelligence pour les h\u00f4pitaux',
    heroTitle: 'Un seul geste, du stationnement \u00e0 la fondation.',
    heroSub: 'Trouver son chemin. Demander un pr\u00e9pos\u00e9 ou la s\u00e9curit\u00e9. Payer le stationnement. Partager un avis. Donner \u00e0 la fondation. Sans application, sans connexion.',
    heroCta: 'R\u00e9server une d\u00e9monstration',
    counter0: 'applications, connexions ou t\u00e9l\u00e9chargements',
    counter1: 'Aucun GPS requis pour commencer',
    counter2: 'geste pour demander un pr\u00e9pos\u00e9 ou la s\u00e9curit\u00e9',
    counter3: 'Anglais, fran\u00e7ais, espagnol',

    oppLabel: 'L\'occasion',
    oppTitle: 'Une visite \u00e0 l\'h\u00f4pital comporte trop de petites frictions.',
    oppText: 'Les h\u00f4pitaux sont grands, d\u00e9routants et stressants. Les patients arrivent anxieux et en retard, et le personnel est d\u00e9tourn\u00e9 de son travail pour donner des indications. Demander un pr\u00e9pos\u00e9, joindre la s\u00e9curit\u00e9, payer le stationnement, partager un commentaire ou donner \u00e0 la fondation \u2014 chaque besoin passe par un comptoir, un num\u00e9ro ou un syst\u00e8me diff\u00e9rent.',
    oppIdea1Title: 'Trouver son chemin',
    oppIdea1: 'Les patients et visiteurs voient o\u00f9 ils se trouvent et obtiennent un itin\u00e9raire clair vers leur destination.',
    oppIdea2Title: 'Obtenir de l\'aide rapidement',
    oppIdea2: 'Une demande non clinique pour un pr\u00e9pos\u00e9, la s\u00e9curit\u00e9 ou de l\'aide g\u00e9n\u00e9rale rejoint la bonne \u00e9quipe, avec la localisation exacte.',
    oppIdea3Title: 'Boucler la boucle',
    oppIdea3: 'Stationnement, avis, commentaires et dons \u00e0 la fondation \u2014 le tout \u00e0 partir du m\u00eame point de contact.',

    howLabel: 'Comment \u00e7a fonctionne',
    howTitle: 'Un r\u00e9seau de points de contact connect\u00e9s pour tout l\'h\u00f4pital.',
    howStep1Title: 'Toucher',
    howStep1: 'Touchez un point de contact ou balayez un code. Rien \u00e0 installer. Une page claire et multilingue s\'ouvre et sait exactement o\u00f9 vous \u00eates.',
    howStep2Title: 'Choisir',
    howStep2: 'Trouver son chemin, demander de l\'aide, payer le stationnement, partager un avis ou donner \u00e0 la fondation.',
    howStep3Title: 'Contr\u00f4ler',
    howStep3: 'Les demandes atteignent la bonne \u00e9quipe avec la localisation. Un seul centre de commande voit tout.',
    howTouchpoints: 'Points de contact aux entr\u00e9es, au stationnement, aux ascenseurs, dans les corridors et les salles d\'attente.',

    foundLabel: 'La fondation',
    foundTitle: 'Chaque hall, salle d\'attente et ascenseur devient un moment pour donner.',
    foundP1Title: 'Un don \u00e0 un geste',
    foundP1: 'Placez des points de contact pour les dons l\u00e0 o\u00f9 les gens ont un moment et une raison de donner.',
    foundP2Title: 'Moments de gratitude',
    foundP2: 'Les patients et les familles ayant v\u00e9cu une bonne exp\u00e9rience sont \u00e0 un geste de redonner.',
    foundP3Title: 'La relation donateur reste la v\u00f4tre',
    foundP3: 'Le don passe par la page de dons de la fondation, qui conserve le contr\u00f4le de la relation avec le donateur.',
    foundP4Title: 'Rapports par emplacement',
    foundP4: 'Voyez quels points de contact suscitent le plus d\'int\u00e9r\u00eat, sans donn\u00e9es personnelles d\u00e9tenues par Tapelligence.',
    foundP5Title: 'Simple et multilingue',
    foundP5: 'Un parcours de don court en anglais, fran\u00e7ais ou espagnol.',

    audLabel: 'Qui en b\u00e9n\u00e9ficie',
    audTitle: 'Con\u00e7u pour tous ceux que l\'h\u00f4pital touche.',
    audTab1: 'H\u00f4pital et administration',
    audTab2: 'Patients, visiteurs et proches aidants',
    audTab3: 'Personnel',
    audTab4: 'La fondation',
    audHosp: [
      'Meilleures premi\u00e8res impressions \u2014 les patients et les familles se sentent guid\u00e9s d\u00e8s le stationnement.',
      'Moins de retards et de mauvais parcours vers les rendez-vous.',
      'De meilleurs avis \u2014 les patients satisfaits sont invit\u00e9s \u00e0 laisser un avis public; les pr\u00e9occupations sont transmises en priv\u00e9.',
      'Donn\u00e9es op\u00e9rationnelles \u2014 destinations populaires, points de contact achaland\u00e9s et demandes d\'aide, sans donn\u00e9es personnelles.',
      'Un journal d\'audit complet pour chaque demande, modification et action.',
    ],
    audPatients: [
      'Touchez et \u00e7a fonctionne \u2014 aucune application, compte ou mot de passe.',
      'Sachez o\u00f9 vous \u00eates \u2014 le point de contact indique au t\u00e9l\u00e9phone votre position exacte.',
      'Un itin\u00e9raire clair avec la distance et le temps de marche estim\u00e9.',
      'Itin\u00e9raires accessibles, contraste \u00e9lev\u00e9 et \u00e9crans simples.',
      'Demandez de l\'aide \u2014 un pr\u00e9pos\u00e9 ou la s\u00e9curit\u00e9, sans chercher quelqu\'un.',
      'Payez le stationnement \u00e0 partir du t\u00e9l\u00e9phone en sortant.',
      'Un avis rapide ou un commentaire priv\u00e9, au moment m\u00eame.',
    ],
    audStaff: [
      'Les demandes de pr\u00e9pos\u00e9 arrivent avec la localisation exacte et un accus\u00e9 de r\u00e9ception.',
      'La s\u00e9curit\u00e9 re\u00e7oit les demandes d\'assistance non urgente avec la localisation.',
      'Moins de questions \u00ab o\u00f9 se trouve\u2026? \u00bb \u2014 les gens se d\u00e9brouillent seuls.',
      'Les cartes sont mises \u00e0 jour depuis un outil d\'administration simple, con\u00e7u pour du personnel non technique.',
      'Acc\u00e8s par r\u00f4le \u2014 chaque personne voit uniquement ce que son r\u00f4le n\u00e9cessite.',
    ],
    audFound: 'L\'ensemble du parcours de don se trouve dans la section Fondation ci-dessus.',

    capLabel: 'Capacit\u00e9s de la plateforme',
    capTitle: 'Une plateforme, chaque point de contact.',
    caps: [
      { title: 'Points de contact', desc: '\u00c9tiquettes NFC et codes QR discrets aux entr\u00e9es, au stationnement, aux ascenseurs, dans les corridors et les salles d\'attente.' },
      { title: 'Trouver son chemin', desc: 'Voir la position exacte sur une carte sans GPS, puis choisir une destination.' },
      { title: 'Guidez-moi', desc: 'Itin\u00e9raire int\u00e9rieur \u00e9tape par \u00e9tape avec distance et temps de marche estim\u00e9.' },
      { title: 'Itin\u00e9raire accessible', desc: 'Parcours accessibles en fauteuil roulant, contraste \u00e9lev\u00e9 et \u00e9crans simples.' },
      { title: 'Demander un pr\u00e9pos\u00e9', desc: 'Une demande non clinique avec la localisation exacte et un accus\u00e9 de r\u00e9ception.' },
      { title: 'Demander la s\u00e9curit\u00e9', desc: 'Assistance non urgente avec la localisation.' },
      { title: 'Aide g\u00e9n\u00e9rale', desc: 'Une demande d\'aide non clinique, achemin\u00e9e vers l\'accueil ou les b\u00e9n\u00e9voles.' },
      { title: 'Payer le stationnement', desc: 'Connexion optionnelle au syst\u00e8me de stationnement de l\'h\u00f4pital.' },
      { title: 'Avis et commentaires', desc: 'Avis publics pour les patients satisfaits; pr\u00e9occupations priv\u00e9es pour r\u00e9solution rapide.' },
      { title: 'Don \u00e0 la fondation', desc: 'Un parcours de don simple vers la page de la fondation.' },
      { title: 'Administration des cartes', desc: 'Le personnel met \u00e0 jour les destinations et les points de contact sans d\u00e9veloppeur.' },
      { title: 'Centre de commande', desc: 'Vue en direct des demandes, destinations populaires et points de contact achaland\u00e9s.' },
      { title: 'R\u00f4les et gouvernance', desc: 'Super administrateur, administrateur client, gestionnaire de site, personnel et observateur.' },
      { title: 'Positionnement en direct', desc: 'Ajout optionnel avec balises ou Wi-Fi pour un point bleu en direct et r\u00e9acheminement.', roadmap: true },
    ],

    journeyLabel: 'Le parcours',
    journeyTitle: 'Du stationnement \u00e0 la fondation.',
    journeySteps: ['Arriv\u00e9e', 'Trouver son chemin', 'Demander de l\'aide', 'Payer le stationnement', 'Partager', 'Redonner'],

    whoLabel: '\u00c0 qui s\'adresse Tapelligence',
    whoTitle: 'De l\'h\u00f4pital communautaire au r\u00e9seau de sant\u00e9.',
    who: [
      { type: 'H\u00f4pitaux communautaires', points: ['D\u00e9marrage rapide aux entr\u00e9es cl\u00e9s', 'Moins de patients \u00e9gar\u00e9s', 'L\u00e9ger en personnel et en TI'] },
      { type: 'Grands h\u00f4pitaux et h\u00f4pitaux universitaires', points: ['Itin\u00e9raires multi-b\u00e2timents', 'Ailes, \u00e9tages et ascenseurs', 'Administration centralis\u00e9e des cartes'] },
      { type: 'R\u00e9seaux de sant\u00e9', points: ['Un tableau de bord, chaque \u00e9tablissement', 'Normes de marque partag\u00e9es', 'Acc\u00e8s par r\u00f4le et par site'] },
    ],

    trustLabel: 'Confiance, vie priv\u00e9e et gouvernance',
    trustTitle: 'Con\u00e7u pour un environnement sensible.',
    trustItems: [
      { title: 'Non clinique par conception', text: 'Aucun avis m\u00e9dical et aucun d\u00e9tail clinique recueilli. Ne remplace pas les services d\'urgence.' },
      { title: 'Vie priv\u00e9e par conception', text: 'Aucun compte. La localisation provient uniquement du point de contact que la personne choisit de toucher, et rien ne suit les individus.' },
      { title: 'Contr\u00f4le d\'acc\u00e8s', text: 'Les r\u00f4les sont appliqu\u00e9s sur le serveur, pas seulement masqu\u00e9s dans l\'interface.' },
      { title: 'Journal d\'audit complet', text: 'Chaque modification de carte, demande et action est consign\u00e9e.' },
      { title: 'Les paiements et dons restent les v\u00f4tres', text: 'Le stationnement et les dons passent par les propres syst\u00e8mes de l\'h\u00f4pital.' },
    ],
    trustComplement: 'Fonctionne aux c\u00f4t\u00e9s de la signalisation et des syst\u00e8mes existants. Aucune int\u00e9gration au DM\u00c9 ou au syst\u00e8me de rendez-vous n\'est requise pour commencer.',

    nextLabel: 'Prochaines \u00e9tapes',
    nextTitle: 'Un parcours simple, de la conversation aux r\u00e9sultats.',
    nextSteps: [
      { title: 'D\u00e9monstration', desc: 'Une d\u00e9monstration de 20 minutes adapt\u00e9e \u00e0 votre h\u00f4pital.' },
      { title: 'Projet pilote', desc: 'Une entr\u00e9e et les itin\u00e9raires cl\u00e9s dans un b\u00e2timent.' },
      { title: 'Bilan \u00e0 90 jours', desc: 'Utilisation, demandes, avis, dons et r\u00e9troaction du personnel.' },
      { title: 'D\u00e9ploiement', desc: '\u00c9tendre aux ailes, b\u00e2timents et campus.' },
    ],
    nextClosing: 'Commen\u00e7ons par un projet pilote.',
    pdfBtn: 'T\u00e9l\u00e9charger le sommaire ex\u00e9cutif (anglais)',
    pdfNote: '',

    smallPrint: [
      'Non destin\u00e9 aux urgences m\u00e9dicales ni \u00e0 un usage clinique. Les demandes de s\u00e9curit\u00e9 visent l\'assistance non urgente.',
      'La disponibilit\u00e9 et la configuration des fonctionnalit\u00e9s sont d\u00e9finies par h\u00f4pital; certaines capacit\u00e9s, dont les connexions de paiement de stationnement, sont optionnelles, pr\u00e9vues ou d\u00e9pendantes d\'une int\u00e9gration.',
      'Les r\u00e8gles de confidentialit\u00e9 et de donn\u00e9es varient selon la province, l\'\u00c9tat et l\'organisme de r\u00e9glementation; les h\u00f4pitaux demeurent responsables des param\u00e8tres qu\'ils activent et doivent confirmer les exigences localement.',
      'L\'impact est mesur\u00e9 pendant le pilote et n\'est pas garanti.',
    ],
    roadmapBadge: 'Feuille de route',
    optionalBadge: 'Optionnel',

    vizCommandCenter: 'Centre de commande',
    vizBusiest: 'Points les plus achaland\u00e9s',
    vizTopDest: 'Destinations populaires',
    vizActive: 'Demandes actives',
    vizTeams: '\u00c9quipes en ligne',
    vizStaff: 'Personnel',
    vizSecurity: 'S\u00e9curit\u00e9',
    vizEvs: 'Entretien',
    vizVolunteers: 'B\u00e9n\u00e9voles',
    vizYouAreHere: 'Vous \u00eates ici',
    vizDestination: 'Destination',
    vizWalk: 'Marche',
    vizNewRequest: 'Nouvelle demande',
    vizOrderly: 'Demande de pr\u00e9pos\u00e9',
    vizLocationAttached: 'Lieu attach\u00e9',
    vizAcknowledge: 'Accuser r\u00e9ception',
    vizTapToGive: 'Touchez pour donner',
    vizGiveTitle: 'Donner \u00e0 la fondation',
    vizGiveBtn: 'Donner',
    vizFoundStoryBtn: 'Voir le parcours fondation',
    vizPending: 'en attente',
    vizEntrance: 'Entr\u00e9e',
    vizLobby: 'Hall',
    vizWaitingRoom: 'Salle d\'attente',
    vizElevator: 'Ascenseur',
  },

  es: {
    heroLabel: 'Tapelligence para hospitales',
    heroTitle: 'Un solo toque, del estacionamiento a la fundaci\u00f3n.',
    heroSub: 'Encontrar el camino. Pedir un camillero o seguridad. Pagar el estacionamiento. Compartir una opini\u00f3n. Donar a la fundaci\u00f3n. Sin aplicaci\u00f3n, sin inicio de sesi\u00f3n.',
    heroCta: 'Reservar una demostraci\u00f3n',
    counter0: 'aplicaciones, inicios de sesi\u00f3n o descargas',
    counter1: 'Sin GPS para comenzar',
    counter2: 'toque para pedir un camillero o seguridad',
    counter3: 'Ingl\u00e9s, franc\u00e9s, espa\u00f1ol',

    oppLabel: 'La oportunidad',
    oppTitle: 'Una visita al hospital tiene demasiadas peque\u00f1as fricciones.',
    oppText: 'Los hospitales son grandes, confusos y estresantes. Los pacientes llegan ansiosos y tarde, y el personal es apartado de su trabajo para dar indicaciones. Pedir un camillero, contactar a seguridad, pagar el estacionamiento, compartir un comentario o donar a la fundaci\u00f3n \u2014 cada necesidad requiere un mostrador, n\u00famero o sistema diferente.',
    oppIdea1Title: 'Encontrar el camino',
    oppIdea1: 'Los pacientes y visitantes ven d\u00f3nde est\u00e1n y obtienen una ruta clara hacia su destino.',
    oppIdea2Title: 'Obtener ayuda r\u00e1pido',
    oppIdea2: 'Una solicitud no cl\u00ednica de un camillero, seguridad o ayuda general llega al equipo correcto, con la ubicaci\u00f3n exacta.',
    oppIdea3Title: 'Cerrar el ciclo',
    oppIdea3: 'Estacionamiento, opiniones, comentarios y donaciones a la fundaci\u00f3n \u2014 todo desde el mismo punto de contacto.',

    howLabel: 'C\u00f3mo funciona',
    howTitle: 'Una red de puntos de contacto conectados para todo el hospital.',
    howStep1Title: 'Tocar',
    howStep1: 'Toque un punto de contacto o escanee un c\u00f3digo. Nada que instalar. Se abre una p\u00e1gina clara y multiling\u00fce que sabe exactamente d\u00f3nde usted se encuentra.',
    howStep2Title: 'Elegir',
    howStep2: 'Encontrar el camino, pedir ayuda, pagar el estacionamiento, compartir una opini\u00f3n o donar a la fundaci\u00f3n.',
    howStep3Title: 'Controlar',
    howStep3: 'Las solicitudes llegan al equipo correcto con la ubicaci\u00f3n. Un solo centro de comando lo ve todo.',
    howTouchpoints: 'Puntos de contacto en entradas, estacionamiento, ascensores, pasillos y salas de espera.',

    foundLabel: 'La fundaci\u00f3n',
    foundTitle: 'Cada vest\u00edbulo, sala de espera y ascensor se convierte en un momento para donar.',
    foundP1Title: 'Una donaci\u00f3n a un toque',
    foundP1: 'Coloque puntos de contacto para donaciones donde la gente tiene un momento y una raz\u00f3n para dar.',
    foundP2Title: 'Momentos de gratitud',
    foundP2: 'Los pacientes y familias que tuvieron una buena experiencia est\u00e1n a un toque de retribuir.',
    foundP3Title: 'La relaci\u00f3n con el donante sigue siendo suya',
    foundP3: 'La donaci\u00f3n va a la p\u00e1gina de donaciones de la fundaci\u00f3n, que mantiene el control de la relaci\u00f3n con el donante.',
    foundP4Title: 'Reportes por ubicaci\u00f3n',
    foundP4: 'Vea qu\u00e9 puntos de contacto generan m\u00e1s inter\u00e9s, sin datos personales en manos de Tapelligence.',
    foundP5Title: 'Simple y multiling\u00fce',
    foundP5: 'Un breve recorrido de donaci\u00f3n en ingl\u00e9s, franc\u00e9s o espa\u00f1ol.',

    audLabel: 'Qui\u00e9n se beneficia',
    audTitle: 'Dise\u00f1ado para todos los que el hospital atiende.',
    audTab1: 'Hospital y administraci\u00f3n',
    audTab2: 'Pacientes, visitantes y cuidadores',
    audTab3: 'Personal',
    audTab4: 'La fundaci\u00f3n',
    audHosp: [
      'Mejores primeras impresiones \u2014 los pacientes y familias se sienten guiados desde el estacionamiento.',
      'Menos retrasos y rutas equivocadas hacia las citas.',
      'Mejores opiniones \u2014 los pacientes satisfechos son invitados a dejar una opini\u00f3n p\u00fablica; las preocupaciones se transmiten en privado.',
      'Datos operativos \u2014 destinos populares, puntos de contacto concurridos y solicitudes de ayuda, sin datos personales.',
      'Un registro de auditor\u00eda completo para cada solicitud, cambio y acci\u00f3n.',
    ],
    audPatients: [
      'Toque y funciona \u2014 sin aplicaci\u00f3n, cuenta o contrase\u00f1a.',
      'Sepa d\u00f3nde est\u00e1 \u2014 el punto de contacto le indica al tel\u00e9fono su posici\u00f3n exacta.',
      'Una ruta clara con distancia y tiempo estimado de caminata.',
      'Rutas accesibles, alto contraste y pantallas simples.',
      'Pida ayuda \u2014 un camillero o seguridad, sin buscar a alguien.',
      'Pague el estacionamiento desde el tel\u00e9fono al salir.',
      'Una opini\u00f3n r\u00e1pida o un comentario privado, en el momento.',
    ],
    audStaff: [
      'Las solicitudes de camillero llegan con la ubicaci\u00f3n exacta y un acuse de recibo.',
      'Seguridad recibe las solicitudes de asistencia no urgente con la ubicaci\u00f3n.',
      'Menos preguntas "\u00bfd\u00f3nde queda\u2026?" \u2014 las personas se orientan solas.',
      'Los mapas se actualizan desde una herramienta de administraci\u00f3n simple, dise\u00f1ada para personal no t\u00e9cnico.',
      'Acceso por rol \u2014 cada persona ve solo lo que su rol necesita.',
    ],
    audFound: 'El recorrido completo de donaci\u00f3n se encuentra en la secci\u00f3n Fundaci\u00f3n m\u00e1s arriba.',

    capLabel: 'Capacidades de la plataforma',
    capTitle: 'Una plataforma, cada punto de contacto.',
    caps: [
      { title: 'Puntos de contacto', desc: 'Etiquetas NFC y c\u00f3digos QR discretos en entradas, estacionamiento, ascensores, pasillos y salas de espera.' },
      { title: 'Encontrar el camino', desc: 'Ver la ubicaci\u00f3n exacta en un mapa sin GPS, luego elegir un destino.' },
      { title: 'Ll\u00e9vame all\u00ed', desc: 'Ruta interior paso a paso con distancia y tiempo estimado de caminata.' },
      { title: 'Ruta accesible', desc: 'Rutas accesibles para sillas de ruedas, alto contraste y pantallas simples.' },
      { title: 'Solicitar un camillero', desc: 'Una solicitud no cl\u00ednica con la ubicaci\u00f3n exacta y acuse de recibo.' },
      { title: 'Solicitar seguridad', desc: 'Asistencia no urgente con la ubicaci\u00f3n.' },
      { title: 'Ayuda general', desc: 'Una solicitud de ayuda no cl\u00ednica, dirigida a recepci\u00f3n o voluntarios.' },
      { title: 'Pagar estacionamiento', desc: 'Conexi\u00f3n opcional al sistema de estacionamiento del hospital.' },
      { title: 'Opiniones y comentarios', desc: 'Opiniones p\u00fablicas para pacientes satisfechos; preocupaciones privadas para resoluci\u00f3n r\u00e1pida.' },
      { title: 'Donar a la fundaci\u00f3n', desc: 'Un recorrido de donaci\u00f3n simple hacia la p\u00e1gina de la fundaci\u00f3n.' },
      { title: 'Administraci\u00f3n de mapas', desc: 'El personal actualiza destinos y puntos de contacto sin un desarrollador.' },
      { title: 'Centro de comando', desc: 'Vista en vivo de solicitudes, destinos populares y puntos de contacto concurridos.' },
      { title: 'Roles y gobernanza', desc: 'Super administrador, administrador cliente, gerente de sitio, personal y observador.' },
      { title: 'Posicionamiento en vivo', desc: 'Mejora opcional con balizas o Wi-Fi para un punto azul en vivo y reencaminamiento.', roadmap: true },
    ],

    journeyLabel: 'El recorrido',
    journeyTitle: 'Del estacionamiento a la fundaci\u00f3n.',
    journeySteps: ['Llegar', 'Encontrar el camino', 'Pedir ayuda', 'Pagar estacionamiento', 'Compartir', 'Retribuir'],

    whoLabel: 'Para qui\u00e9n es',
    whoTitle: 'Del hospital comunitario al sistema de salud.',
    who: [
      { type: 'Hospitales comunitarios', points: ['Inicio r\u00e1pido en entradas clave', 'Menos pacientes perdidos', 'Liviano en personal y TI'] },
      { type: 'Hospitales grandes y universitarios', points: ['Rutas entre edificios', 'Alas, pisos y ascensores', 'Administraci\u00f3n centralizada de mapas'] },
      { type: 'Sistemas de salud', points: ['Un tablero, cada sede', 'Est\u00e1ndares de marca compartidos', 'Acceso por rol y por sede'] },
    ],

    trustLabel: 'Confianza, privacidad y gobernanza',
    trustTitle: 'Dise\u00f1ado para un entorno sensible.',
    trustItems: [
      { title: 'No cl\u00ednico por dise\u00f1o', text: 'Sin consejos m\u00e9dicos ni detalles cl\u00ednicos recopilados. No sustituye los servicios de emergencia.' },
      { title: 'Privacidad por dise\u00f1o', text: 'Sin cuenta. La ubicaci\u00f3n proviene solo del punto de contacto que la persona elige tocar, y nada rastrea a los individuos.' },
      { title: 'Control de acceso', text: 'Los roles se aplican en el servidor, no solo se ocultan en la interfaz.' },
      { title: 'Registro de auditor\u00eda completo', text: 'Cada cambio de mapa, solicitud y acci\u00f3n queda registrada.' },
      { title: 'Los pagos y donaciones quedan con usted', text: 'El estacionamiento y las donaciones pasan por los propios sistemas del hospital.' },
    ],
    trustComplement: 'Funciona junto con la se\u00f1alizaci\u00f3n y los sistemas existentes. No se necesita integraci\u00f3n con el HCE o el sistema de citas para comenzar.',

    nextLabel: 'Pr\u00f3ximos pasos',
    nextTitle: 'Un camino simple de la conversaci\u00f3n a los resultados.',
    nextSteps: [
      { title: 'Demostraci\u00f3n', desc: 'Una demostraci\u00f3n de 20 minutos adaptada a su hospital.' },
      { title: 'Piloto', desc: 'Una entrada y las rutas clave en un edificio.' },
      { title: 'Revisi\u00f3n a 90 d\u00edas', desc: 'Uso, solicitudes, opiniones, donaciones y retroalimentaci\u00f3n del personal.' },
      { title: 'Despliegue', desc: 'Extender a alas, edificios y campus.' },
    ],
    nextClosing: 'Comencemos con un piloto.',
    pdfBtn: 'Descargar el resumen ejecutivo (ingl\u00e9s)',
    pdfNote: '',

    smallPrint: [
      'No destinado a emergencias m\u00e9dicas ni uso cl\u00ednico. Las solicitudes de seguridad son para asistencia no urgente.',
      'La disponibilidad y configuraci\u00f3n de funcionalidades se definen por hospital; algunas capacidades, incluyendo conexiones de pago de estacionamiento, son opcionales, planificadas o sujetas a integraci\u00f3n.',
      'Las normas de privacidad y datos var\u00edan seg\u00fan la provincia, estado y regulador; los hospitales son responsables de la configuraci\u00f3n que habilitan y deben confirmar los requisitos localmente.',
      'El impacto se mide durante el piloto y no est\u00e1 garantizado.',
    ],
    roadmapBadge: 'Hoja de ruta',
    optionalBadge: 'Opcional',

    vizCommandCenter: 'Centro de comando',
    vizBusiest: 'Puntos m\u00e1s concurridos',
    vizTopDest: 'Destinos populares',
    vizActive: 'Solicitudes activas',
    vizTeams: 'Equipos en l\u00ednea',
    vizStaff: 'Personal',
    vizSecurity: 'Seguridad',
    vizEvs: 'Limpieza',
    vizVolunteers: 'Voluntarios',
    vizYouAreHere: 'Usted est\u00e1 aqu\u00ed',
    vizDestination: 'Destino',
    vizWalk: 'Caminata',
    vizNewRequest: 'Nueva solicitud',
    vizOrderly: 'Solicitud de camillero',
    vizLocationAttached: 'Ubicaci\u00f3n adjunta',
    vizAcknowledge: 'Confirmar',
    vizTapToGive: 'Toque para donar',
    vizGiveTitle: 'Donar a la fundaci\u00f3n',
    vizGiveBtn: 'Donar',
    vizFoundStoryBtn: 'Ver el recorrido fundaci\u00f3n',
    vizPending: 'pendientes',
    vizEntrance: 'Entrada',
    vizLobby: 'Vest\u00edbulo',
    vizWaitingRoom: 'Sala de espera',
    vizElevator: 'Ascensor',
  },
};

/* ================================================================
   ICON MAP for capabilities grid
   ================================================================ */
const CAP_ICONS = [
  Smartphone, MapPin, Navigation, Accessibility, HandHelping, Shield,
  HelpCircle, CreditCard, MessageSquare, Gift, Settings, Eye, UserCog, Radio,
];

/* ================================================================
   ANIMATED COUNTER
   ================================================================ */
const AnimatedCounter = ({ end, label, prefix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const duration = 1200;
        const steps = 30;
        const inc = end / steps;
        let cur = 0;
        const iv = setInterval(() => {
          cur += inc;
          if (cur >= end) { setCount(end); clearInterval(iv); }
          else setCount(Math.round(cur));
        }, duration / steps);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end]);

  return (
    <div ref={ref} className="text-center">
      <span className="block text-4xl md:text-5xl font-bold text-[#0077FF]">{prefix}{count}</span>
      <span className="text-sm text-slate-600 mt-1 block">{label}</span>
    </div>
  );
};

/* ================================================================
   TAB VISUAL — Command Center (Hospital & Administration)
   Uses a screenshot of the Tapelligence Experience Manager with
   St-Mary's Hospital data (generated, no real data).
   ================================================================ */
const CommandCenterViz = ({ t }) => (
  <div className="w-full max-w-md mx-auto" data-testid="viz-command-center">
    <div className="rounded-2xl overflow-hidden shadow-2xl border border-slate-700/40">
      <img src="/healthcare_command_center.jpg" alt="Tapelligence Command Center — St-Mary's Hospital"
        className="w-full h-auto" loading="lazy" />
    </div>
  </div>
);

/* ================================================================
   TAB VISUAL — Wayfinding Phone (Patients, Visitors & Caregivers)
   ================================================================ */
const WayfindingPhoneViz = ({ t }) => (
  <div className="flex justify-center" data-testid="viz-wayfinding">
    <div className="relative w-52 md:w-56">
      {/* Phone frame */}
      <div className="bg-slate-900 rounded-[2.2rem] p-2.5 shadow-2xl">
        <div className="bg-white rounded-[1.7rem] overflow-hidden">
          {/* Status bar */}
          <div className="h-6 bg-slate-50 flex items-center justify-center">
            <div className="w-16 h-1.5 rounded-full bg-slate-900" />
          </div>
          {/* Map area */}
          <div className="relative h-56 bg-gradient-to-b from-sky-50 to-slate-100 overflow-hidden">
            {/* Floor grid lines */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 220" fill="none">
              {/* Grid */}
              <line x1="0" y1="55" x2="200" y2="55" stroke="#e2e8f0" strokeWidth="0.5" />
              <line x1="0" y1="110" x2="200" y2="110" stroke="#e2e8f0" strokeWidth="0.5" />
              <line x1="0" y1="165" x2="200" y2="165" stroke="#e2e8f0" strokeWidth="0.5" />
              <line x1="50" y1="0" x2="50" y2="220" stroke="#e2e8f0" strokeWidth="0.5" />
              <line x1="100" y1="0" x2="100" y2="220" stroke="#e2e8f0" strokeWidth="0.5" />
              <line x1="150" y1="0" x2="150" y2="220" stroke="#e2e8f0" strokeWidth="0.5" />
              {/* Corridor shapes */}
              <rect x="20" y="30" width="65" height="40" rx="3" fill="#e2e8f0" />
              <rect x="115" y="30" width="65" height="40" rx="3" fill="#e2e8f0" />
              <rect x="20" y="130" width="65" height="40" rx="3" fill="#e2e8f0" />
              <rect x="115" y="130" width="65" height="40" rx="3" fill="#e2e8f0" />
              {/* Route path — draws itself */}
              <path d="M100,185 L100,150 L100,100 L155,100 L155,48"
                stroke="#0077FF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                strokeDasharray="260" fill="none" className="hc-draw" />
              {/* Destination dot */}
              <circle cx="155" cy="48" r="7" fill="#E8720C" opacity="0.2">
                <animate attributeName="r" values="7;11;7" dur="2s" repeatCount="indefinite" />
              </circle>
              <circle cx="155" cy="48" r="5" fill="#E8720C" />
              {/* You-are-here dot */}
              <circle cx="100" cy="185" r="7" fill="#0077FF" opacity="0.25">
                <animate attributeName="r" values="7;12;7" dur="2s" repeatCount="indefinite" />
              </circle>
              <circle cx="100" cy="185" r="5" fill="#0077FF" />
              <circle cx="100" cy="185" r="2" fill="white" />
            </svg>
            {/* Labels */}
            <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1 shadow-sm border border-slate-200/60">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-[#0077FF]" />
                <span className="text-[9px] font-semibold text-slate-800">{t.vizYouAreHere}</span>
              </div>
            </div>
            <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1 shadow-sm border border-slate-200/60">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-[#E8720C]" />
                <span className="text-[9px] font-semibold text-slate-800">{t.vizDestination}</span>
              </div>
            </div>
          </div>
          {/* Bottom bar — walking time */}
          <div className="px-3 py-2.5 bg-white border-t border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Navigation className="w-3.5 h-3.5 text-[#0077FF]" />
              <span className="text-[10px] font-semibold text-slate-800">{t.vizWalk}</span>
            </div>
            <div className="bg-[#0077FF]/10 rounded-full px-2 py-0.5">
              <span className="text-[10px] font-bold text-[#0077FF]">{'~ min'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ================================================================
   TAB VISUAL — Staff Tablet (Staff)
   ================================================================ */
const StaffTabletViz = ({ t }) => (
  <div className="flex justify-center" data-testid="viz-staff-tablet">
    <div className="w-full max-w-xs">
      {/* Tablet frame */}
      <div className="bg-slate-200 rounded-2xl p-3 shadow-2xl">
        <div className="bg-white rounded-xl overflow-hidden">
          {/* App header */}
          <div className="bg-slate-900 px-4 py-2.5 flex items-center justify-between">
            <span className="text-[10px] text-white font-medium tracking-wide">{t.vizCommandCenter}</span>
            <span className="w-2 h-2 rounded-full bg-green-400" />
          </div>
          {/* Notification card — slides in */}
          <div className="p-3">
            <div className="hc-slidein bg-[#0077FF]/5 border border-[#0077FF]/20 rounded-xl p-4">
              {/* Badge */}
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-[#0077FF] flex items-center justify-center">
                  <Bell className="w-3.5 h-3.5 text-white" />
                </div>
                <div>
                  <span className="text-[10px] font-bold text-slate-900 block">{t.vizNewRequest}</span>
                  <span className="text-[9px] text-slate-500">{t.vizOrderly}</span>
                </div>
              </div>
              {/* Location */}
              <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 mb-3 border border-slate-100">
                <MapPin className="w-3.5 h-3.5 text-[#0077FF] flex-shrink-0" />
                <span className="text-[10px] text-slate-700 font-medium">{t.vizLocationAttached}</span>
              </div>
              {/* Acknowledge button */}
              <button className="w-full bg-[#0077FF] text-white text-[11px] font-semibold py-2.5 rounded-lg flex items-center justify-center gap-1.5 transition-transform hover:scale-[1.02] active:scale-[0.98]">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {t.vizAcknowledge}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ================================================================
   TAB VISUAL — Foundation (warm family gratitude image + heart)
   Image: AI-generated (Gemini 3.1 Flash) — couple with child outside
   hospital. No devices, no names, no logos, no identifiable faces
   presented as real patients.
   ================================================================ */
const FoundationViz = ({ t }) => (
  <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full" data-testid="viz-foundation">
    <img src="/healthcare_foundation_family.jpg" alt=""
      className="w-full h-72 md:h-80 object-cover" loading="lazy" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
    {/* Single soft pulsing heart in corner */}
    <div className="absolute top-4 right-4 md:top-6 md:right-6">
      <Heart className="w-8 h-8 text-[#E8720C] drop-shadow-lg hc-pulse" fill="#E8720C" fillOpacity={0.3} />
    </div>
  </div>
);

/* ================================================================
   TAB VISUALS ARRAY
   ================================================================ */
const TAB_VISUALS = [CommandCenterViz, WayfindingPhoneViz, StaffTabletViz, FoundationViz];

/* ================================================================
   PAGE COMPONENT
   ================================================================ */
const HealthcarePage = () => {
  const { language } = useLanguage();
  const t = T[language] || T.en;
  const getLocalizedPath = (path) => `/${language}${path}`;
  const [activeTab, setActiveTab] = useState(0);

  const tabContent = [t.audHosp, t.audPatients, t.audStaff, t.audFound];
  const tabLabels = [t.audTab1, t.audTab2, t.audTab3, t.audTab4];

  return (
    <div className="min-h-screen bg-white" data-testid="healthcare-page">
      <AnimStyles />

      {/* ── 1. HERO — two-column like sibling industry pages ── */}
      <section className="pt-36 pb-20 bg-slate-50 border-b border-slate-200" data-testid="healthcare-hero">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <span className="text-xs font-bold text-[#0077FF] uppercase tracking-widest mb-4 block">{t.heroLabel}</span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-testid="healthcare-title">
                {t.heroTitle}
              </h1>
              <p className="text-lg md:text-xl text-slate-500 leading-relaxed mb-8">
                {t.heroSub}
              </p>
              <Link to={getLocalizedPath('/contact')} className="inline-flex items-center gap-2 bg-[#0077FF] text-white px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-[#0066dd] transition-colors shadow-sm hover:shadow-md active:scale-[0.98]" data-testid="healthcare-cta-btn">
                {t.heroCta} <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 lg:mt-0">
                <div className="rounded-2xl overflow-hidden shadow-lg">
                  <img src="/industry_healthcare.jpg" alt="Hospital facility" className="w-full h-80 object-cover" loading="eager" />
                </div>
              </div>
            </Reveal>
          </div>

          {/* Counters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 max-w-4xl mx-auto">
            <AnimatedCounter end={0} prefix="" label={t.counter0} />
            <div className="text-center">
              <span className="block text-4xl md:text-5xl font-bold text-[#0077FF]">&mdash;</span>
              <span className="text-sm text-slate-600 mt-1 block">{t.counter1}</span>
            </div>
            <AnimatedCounter end={1} label={t.counter2} />
            <div className="text-center">
              <span className="block text-4xl md:text-5xl font-bold text-[#0077FF]">3</span>
              <span className="text-sm text-slate-600 mt-1 block">{t.counter3}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. THE OPPORTUNITY ── */}
      <section className="py-20 md:py-28" data-testid="healthcare-opportunity">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <Reveal>
            <div className="max-w-3xl">
              <span className="text-xs font-bold text-[#0077FF] uppercase tracking-widest mb-3 block">{t.oppLabel}</span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 leading-tight">{t.oppTitle}</h2>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed">{t.oppText}</p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8 mt-14">
            {[
              { icon: MapPin, title: t.oppIdea1Title, text: t.oppIdea1 },
              { icon: Phone, title: t.oppIdea2Title, text: t.oppIdea2 },
              { icon: Star, title: t.oppIdea3Title, text: t.oppIdea3 },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 h-full hover:shadow-md transition-shadow">
                  <item.icon className="w-6 h-6 text-[#0077FF] mb-4" />
                  <h3 className="text-sm font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. HOW IT WORKS — animated 3-step flow ── */}
      <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200" data-testid="healthcare-how-it-works">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <Reveal>
            <div className="max-w-3xl">
              <span className="text-xs font-bold text-[#0077FF] uppercase tracking-widest mb-3 block">{t.howLabel}</span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 leading-tight">{t.howTitle}</h2>
            </div>
          </Reveal>

          <div className="relative mt-14 max-w-4xl mx-auto">
            <div className="hidden md:block absolute top-10 left-[16.6%] right-[16.6%] h-0.5 bg-[#0077FF]/20" />
            <div className="grid md:grid-cols-3 gap-10">
              {[
                { num: '1', title: t.howStep1Title, text: t.howStep1, icon: Smartphone },
                { num: '2', title: t.howStep2Title, text: t.howStep2, icon: Navigation },
                { num: '3', title: t.howStep3Title, text: t.howStep3, icon: Eye },
              ].map((s, i) => (
                <Reveal key={i} delay={i * 0.15}>
                  <div className="text-center relative">
                    <div className="w-20 h-20 rounded-full bg-[#0077FF] text-white flex items-center justify-center mx-auto mb-4 relative z-10 shadow-lg shadow-[#0077FF]/20">
                      <s.icon className="w-8 h-8" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{s.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{s.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.3}>
            <p className="text-sm text-slate-500 text-center mt-10 italic">{t.howTouchpoints}</p>
          </Reveal>
        </div>
      </section>

      {/* ── 4. THE FOUNDATION — most striking section after hero ── */}
      <section className="py-20 md:py-28 relative overflow-hidden" data-testid="healthcare-foundation">
        {/* Warm background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#FFF7ED] to-[#FFF1E0]" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23E8720C\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="container-max px-6 md:px-12 lg:px-24 relative">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-14">
              <span className="text-xs font-bold text-[#E8720C] uppercase tracking-widest mb-3 block">{t.foundLabel}</span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 mb-4 leading-tight">{t.foundTitle}</h2>
            </div>
          </Reveal>

          {/* Location icons */}
          <Reveal delay={0.1}>
            <div className="flex justify-center gap-10 md:gap-16 mb-14">
              {[
                { icon: Building2, label: t.vizLobby },
                { icon: Users, label: t.vizWaitingRoom },
                { icon: Layers, label: t.vizElevator },
              ].map((loc, i) => (
                <div key={i} className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-[#E8720C]/10 flex items-center justify-center mx-auto mb-2 shadow-sm">
                    <loc.icon className="w-7 h-7 text-[#E8720C]" />
                  </div>
                  <span className="text-xs text-slate-600 font-medium">{loc.label}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <div className="hc-fg hc-fg3 max-w-5xl mx-auto">
            {[
              { icon: Gift, title: t.foundP1Title, text: t.foundP1 },
              { icon: Heart, title: t.foundP2Title, text: t.foundP2 },
              { icon: Link2, title: t.foundP3Title, text: t.foundP3 },
              { icon: BarChart3, title: t.foundP4Title, text: t.foundP4 },
              { icon: Star, title: t.foundP5Title, text: t.foundP5 },
            ].map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="bg-white/80 backdrop-blur rounded-xl p-6 border border-orange-200/60 shadow-sm hover:shadow-md transition-shadow h-full">
                  <p.icon className="w-5 h-5 text-[#E8720C] mb-3" />
                  <h3 className="text-sm font-bold text-slate-900 mb-1">{p.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. WHO BENEFITS — two-column tabs with visuals ── */}
      <section className="py-20 md:py-28" data-testid="healthcare-audience">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <Reveal>
            <div className="max-w-3xl">
              <span className="text-xs font-bold text-[#0077FF] uppercase tracking-widest mb-3 block">{t.audLabel}</span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 leading-tight">{t.audTitle}</h2>
            </div>
          </Reveal>

          {/* Tab buttons */}
          <div className="flex flex-wrap gap-2 mt-10 mb-8">
            {tabLabels.map((label, i) => (
              <button key={i} onClick={() => setActiveTab(i)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${activeTab === i ? 'bg-[#0077FF] text-white shadow-md shadow-[#0077FF]/20' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                data-testid={`audience-tab-${i}`}>
                {label}
              </button>
            ))}
          </div>

          {/* Tab content — two columns: bullets + visual */}
          <div key={activeTab} className="hc-tabfade bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden" data-testid="audience-content">
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Bullets column */}
              <div className="p-6 md:p-8 flex items-center">
                {activeTab === 3 ? (
                  <div className="w-full flex flex-col justify-center gap-5">
                    <p className="text-sm text-slate-600 italic">{tabContent[activeTab]}</p>
                    <button
                      onClick={() => document.querySelector('[data-testid="healthcare-foundation"]')?.scrollIntoView({ behavior: 'smooth' })}
                      className="inline-flex items-center gap-2 bg-[#E8720C] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#d4660a] transition-colors shadow-sm w-fit"
                      data-testid="foundation-story-btn">
                      <Heart className="w-4 h-4" />
                      {t.vizFoundStoryBtn}
                    </button>
                  </div>
                ) : typeof tabContent[activeTab] === 'string' ? (
                  <p className="text-sm text-slate-600 italic">{tabContent[activeTab]}</p>
                ) : (
                  <ul className="space-y-3 w-full">
                    {tabContent[activeTab].map((point, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="w-4 h-4 text-[#0077FF] mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-700 leading-relaxed">{point}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {/* Visual column */}
              <div className="p-6 md:p-8 bg-slate-100/50 flex items-center justify-center border-t lg:border-t-0 lg:border-l border-slate-200">
                {(() => {
                  const Viz = TAB_VISUALS[activeTab];
                  return <Viz t={t} />;
                })()}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 6. CAPABILITIES GRID — hover motion ── */}
      <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200" data-testid="healthcare-capabilities">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <Reveal>
            <div className="max-w-3xl">
              <span className="text-xs font-bold text-[#0077FF] uppercase tracking-widest mb-3 block">{t.capLabel}</span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 leading-tight">{t.capTitle}</h2>
            </div>
          </Reveal>
          <div className="hc-fg hc-fg4 mt-12">
            {t.caps.map((cap, i) => {
              const Icon = CAP_ICONS[i] || Smartphone;
              return (
                <Reveal key={i} delay={i * 0.04}>
                  <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-200 h-full group">
                    <div className="flex items-start gap-3 mb-2">
                      <Icon className="w-5 h-5 text-[#0077FF] mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform" />
                      <h3 className="text-sm font-bold text-slate-900">{cap.title}</h3>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">{cap.desc}</p>
                    {cap.roadmap && (
                      <span className="inline-block mt-2 text-[10px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">{t.roadmapBadge}</span>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 7. THE JOURNEY — animated path ── */}
      <section className="py-20 md:py-28" data-testid="healthcare-journey">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-14">
              <span className="text-xs font-bold text-[#0077FF] uppercase tracking-widest mb-3 block">{t.journeyLabel}</span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 leading-tight">{t.journeyTitle}</h2>
            </div>
          </Reveal>
          <div className="relative max-w-4xl mx-auto">
            {/* Connecting path */}
            <div className="hidden md:block absolute top-6 left-[8%] right-[8%] h-0.5 bg-gradient-to-r from-[#0077FF] via-[#0077FF]/60 to-[#E8720C]" />
            <div className="grid grid-cols-3 md:grid-cols-6 gap-6">
              {t.journeySteps.map((step, i) => {
                const icons = [Building2, MapPin, HandHelping, CreditCard, MessageSquare, Gift];
                const StepIcon = icons[i];
                const isLast = i === t.journeySteps.length - 1;
                return (
                  <Reveal key={i} delay={i * 0.1}>
                    <div className="text-center relative">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2 relative z-10 shadow-md ${isLast ? 'bg-[#E8720C] text-white shadow-[#E8720C]/20' : 'bg-[#0077FF] text-white shadow-[#0077FF]/20'}`}>
                        <StepIcon className="w-5 h-5" />
                      </div>
                      <span className="text-xs font-medium text-slate-700 block leading-tight">{step}</span>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── 8. WHO IT IS FOR ── */}
      <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200" data-testid="healthcare-who-for">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <Reveal>
            <div className="max-w-3xl">
              <span className="text-xs font-bold text-[#0077FF] uppercase tracking-widest mb-3 block">{t.whoLabel}</span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 leading-tight">{t.whoTitle}</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {t.who.map((w, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-md transition-shadow h-full">
                  <Building2 className="w-6 h-6 text-[#0077FF] mb-4" />
                  <h3 className="text-sm font-bold text-slate-900 mb-3">{w.type}</h3>
                  <ul className="space-y-2">
                    {w.points.map((p, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                        <ChevronRight className="w-3.5 h-3.5 text-[#0077FF] mt-0.5 flex-shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── 9. TRUST, PRIVACY & GOVERNANCE — with shield visual ── */}
      <section className="py-20 md:py-28 relative" data-testid="healthcare-trust">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <Reveal>
            <div className="max-w-3xl">
              <span className="text-xs font-bold text-[#0077FF] uppercase tracking-widest mb-3 block">{t.trustLabel}</span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 leading-tight">{t.trustTitle}</h2>
            </div>
          </Reveal>
          <div className="hc-fg hc-fg3 mt-12">
            {t.trustItems.map((item, i) => {
              const icons = [Shield, Lock, UserCog, FileText, CreditCard];
              const TIcon = icons[i] || Shield;
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="flex items-start gap-4 bg-slate-50 rounded-xl p-5 border border-slate-200 hover:shadow-md transition-shadow h-full">
                    <div className="w-10 h-10 rounded-xl bg-[#0077FF]/10 flex items-center justify-center flex-shrink-0">
                      <TIcon className="w-5 h-5 text-[#0077FF]" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">{item.text}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.3}>
            <p className="text-sm text-slate-500 mt-8 italic max-w-3xl">{t.trustComplement}</p>
          </Reveal>
        </div>
      </section>

      {/* ── 10. NEXT STEPS — animated 4-step roadmap ── */}
      <section className="py-20 md:py-28 bg-slate-900 text-white" data-testid="healthcare-next-steps">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-14">
              <span className="text-xs font-bold text-[#0077FF] uppercase tracking-widest mb-3 block">{t.nextLabel}</span>
              <h2 className="text-2xl md:text-3xl font-bold leading-tight">{t.nextTitle}</h2>
            </div>
          </Reveal>
          <div className="relative max-w-4xl mx-auto">
            <div className="hidden md:block absolute top-8 left-[12.5%] right-[12.5%] h-0.5 bg-white/20" />
            <div className="grid md:grid-cols-4 gap-8">
              {t.nextSteps.map((s, i) => (
                <Reveal key={i} delay={i * 0.12}>
                  <div className="text-center relative">
                    <div className="w-16 h-16 rounded-full bg-[#0077FF] text-white flex items-center justify-center mx-auto mb-4 text-lg font-bold relative z-10 shadow-lg shadow-[#0077FF]/30">
                      {i + 1}
                    </div>
                    <h3 className="text-sm font-bold mb-1">{s.title}</h3>
                    <p className="text-xs text-slate-400 leading-relaxed">{s.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.4}>
            <div className="text-center mt-16">
              <p className="text-xl md:text-2xl font-semibold mb-8">{t.nextClosing}</p>
              <Link to={getLocalizedPath('/contact')} className="inline-flex items-center gap-2 bg-white text-slate-900 px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-slate-100 transition-colors shadow-sm" data-testid="healthcare-cta-btn-bottom">
                {t.heroCta} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>

          {/* PDF Download */}
          <Reveal delay={0.5}>
            <div className="text-center mt-10">
              <a href="/tapelligence-hospitals-executive-summary.pdf" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-white/30 text-white/80 px-6 py-2.5 rounded-full text-xs font-medium hover:bg-white/10 transition-colors"
                data-testid="healthcare-pdf-download">
                <FileText className="w-4 h-4" /> {t.pdfBtn}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── 11. SMALL PRINT ── */}
      <section className="py-10 bg-slate-50 border-t border-slate-200" data-testid="healthcare-small-print">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl mx-auto space-y-2">
            {t.smallPrint.map((line, i) => (
              <p key={i} className="text-[11px] text-slate-400 leading-relaxed">{line}</p>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HealthcarePage;
